import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { getBlockLibraryCliApiBaseUrl } from '@modules/firebase-core/services/BlockLibraryMutationApi';
import type { CliOnboardingThemeSourceKind, CliOnboardingThemeSwitchData } from '@modules/shared-types';

/**
 * The onboarding theme switch, from the editor (onboarding theme switch plan D4).
 *
 * The UI never writes `themeSettingsV2` itself for a switch: it calls the same strict-admin builder route
 * as `segmently themes set-onboarding`, with the signed-in admin's Firebase ID token, so the UI and the
 * CLI copy a theme with the same server code (AC-025).
 */
export interface OnboardingThemeSwitchInput {
  source: CliOnboardingThemeSourceKind;
  themeId: string;
}

export async function switchOnboardingThemeViaApi(
  projectId: string,
  onboardingId: string,
  input: OnboardingThemeSwitchInput,
): Promise<CliOnboardingThemeSwitchData> {
  const user = CoreFirebaseService.getInstance().auth.currentUser;
  if (!user) throw new Error('Sign in before changing the theme.');
  const token = await user.getIdToken();
  const base = getBlockLibraryCliApiBaseUrl().replace(/\/$/, '');
  const response = await fetch(
    `${base}/api/cli/v1/admin/projects/${encodeURIComponent(projectId)}/funnels/${encodeURIComponent(onboardingId)}/theme`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: input.source, themeId: input.themeId }),
    },
  );
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    throw new Error(
      payload?.error?.message
        ?? payload?.detail
        ?? (typeof payload?.error === 'string' ? payload.error : null)
        ?? `Changing the theme failed (HTTP ${response.status}).`,
    );
  }
  const data = payload?.data as CliOnboardingThemeSwitchData | undefined;
  if (data?.onboardingId !== onboardingId || data?.themeId !== input.themeId || data?.source !== input.source) {
    throw new Error('The server returned an unexpected theme change receipt. Refresh before retrying.');
  }
  return data;
}

export const OnboardingThemeApi = {
  switchTheme: switchOnboardingThemeViaApi,
};
